/**
 * Puerta única de lectura para el estado storefront de una sucursal:
 * perfil público + zonas de delivery + estado MP, con TTL 60s y dedupe
 * de llamadas en vuelo. Ver contrato en `infra/storefrontStatusCache.ts`.
 *
 * Reglas para consumidores:
 * - Leer siempre por acá (o por el hook específico), nunca el repo directo
 *   para datos ya cubiertos por este cache.
 * - `force: true` solo cuando el dato va a alimentar una escritura o un
 *   retorno de flujo externo (OAuth MP, publish verification).
 */
import type { StoreProfile } from '../domain/storeProfileTypes';
import type { DeliveryZoneRow } from '@shared/deliveryTypes';
import type { MpIntegrationStatus } from '../../mercadopago/infra/mpSettingsRepo';
import { getStoreProfile } from '../infra/storeProfileRepo';
import { listDeliveryZones } from '../infra/deliveryZonesRepo';
import { getMpIntegrationStatus } from '../../mercadopago/infra/mpSettingsRepo';
import {
  storefrontProfileCache,
  storefrontZonesCache,
  storefrontMpStatusCache,
} from '../infra/storefrontStatusCache';

export {
  invalidateStorefrontStatus,
  invalidateStorefrontZones,
  invalidateStorefrontMpStatus,
  clearStorefrontStatusCaches,
} from '../infra/storefrontStatusCache';

type GetOpts = { force?: boolean };

export function getCachedStoreProfile(
  branchId: string,
  opts?: GetOpts,
): Promise<StoreProfile | null> {
  return storefrontProfileCache.get(branchId, () => getStoreProfile(branchId), opts);
}

export function getCachedDeliveryZones(
  branchId: string,
  opts?: GetOpts,
): Promise<DeliveryZoneRow[]> {
  return storefrontZonesCache.get(branchId, () => listDeliveryZones(branchId), opts);
}

export function getCachedMpIntegrationStatus(
  branchId: string,
  opts?: GetOpts,
): Promise<MpIntegrationStatus> {
  return storefrontMpStatusCache.get(branchId, () => getMpIntegrationStatus(branchId), opts);
}

export type StorefrontStatus = {
  profile: StoreProfile | null;
  zones: DeliveryZoneRow[];
  zonesCount: number;
  mp: MpIntegrationStatus | null;
};

/**
 * Snapshot compuesto. `withZones`/`withMp` controlan qué se carga — el default
 * es solo perfil (el dato que casi todo el mundo necesita).
 */
export async function getStorefrontStatus(
  branchId: string,
  opts?: { withZones?: boolean; withMp?: boolean; force?: boolean },
): Promise<StorefrontStatus> {
  const [profile, zones, mp] = await Promise.all([
    getCachedStoreProfile(branchId, opts),
    opts?.withZones
      ? getCachedDeliveryZones(branchId, opts).catch(() => [] as DeliveryZoneRow[])
      : Promise.resolve([] as DeliveryZoneRow[]),
    opts?.withMp
      ? getCachedMpIntegrationStatus(branchId, opts).catch(() => null)
      : Promise.resolve(null),
  ]);
  return { profile, zones, zonesCount: zones.length, mp };
}

/** `is_public` de la tienda. null = desconocido (error de red). */
export async function isStorePublished(branchId: string): Promise<boolean | null> {
  try {
    return (await getCachedStoreProfile(branchId))?.isPublic === true;
  } catch {
    return null;
  }
}
